import {Checkbox, FormControlLabel, Switch} from '@mui/material'
import {useFormikContext} from 'formik'
import {Edit} from 'lucide-react'
import React from 'react'
import {useLocation} from 'react-router-dom'
import styled from 'styled-components'
import {usePermission} from '~/src/hooks/usePermission'
import {
  HeaderAndButtonContainerBorder,
  SoloSubTabText,
} from '~/src/components/shared/styles/SharedStyles'
import {Button, ButtonContainer} from '~/src/design-system'
import FormikGooglePlacesAutocomplete from '../../ui/autocomplete/FormikGooglePlacesAutocomplete'
import {FormLabel} from '../../ui/form/FormLabel'
import {RequiredAsterisk} from '../../ui/form/RequiredAsterisk'
import {PhoneInput} from '../../ui/inputs/PhoneInput'
import FormikTextField from '../../ui/mui-text-field/FormikTextField'
import {EditButton} from '../company/tabs/styles/FormStyles'

const FormContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 12px 24px 0 rgba(16, 30, 115, 0.06);
`

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
`

const ToggleLabel = styled.span`
  color: #424242;
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 500;
`

const StyledSwitch = styled(Switch)`
  .MuiSwitch-switchBase.Mui-checked {
    color: #03a9fc;
  }
  .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
    background-color: #03a9fc;
  }
`

const StyledCheckbox = styled(Checkbox)`
  color: #03a9fc;
  &.Mui-checked {
    color: #03a9fc;
  }
  & svg {
    color: #03a9fc !important;
  }
  &.Mui-checked svg {
    color: #03a9fc !important;
  }
`

const Divider = styled.div`
  height: 1px;
  background-color: #e0e0e0;
  margin: 32px 0;
`

// Grid replacement components
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 32px;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const PhoneExtensionGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CheckboxContainer = styled.div`
  margin-top: 16px;
`

const AddressContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 0px 32px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`

const MarkupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 24px;
  max-width: 360px;
`

const MarkupInputWrapper = styled.div`
  position: relative;
`

const PercentageSymbol = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #8a8a8a;
  border-left: 1px solid #b3b3b3;
  padding-left: 10px;
  height: 30px;
  display: flex;
  align-items: center;
`

const ColoredTextPleaseEnter = styled.p`
  color: #03a9fc;
  font-size: 14px;
  font-weight: 400;
  margin-top: 16px;
  padding: 0px 32px;
`

interface IFormValues {
  name: string
  phoneNumber: string
  extension?: string
  website?: string
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  isMsp: boolean
  isEor: boolean
  parentAgencyId?: string // New field for hierarchy
  markupPercentage?: number // New field for branch agencies
  address: {
    line1: string
    city: string
    state: string
    zipCode: string
    display: string
  }
  billingAddress: {
    line1: string
    city: string
    state: string
    zipCode: string
    display: string
  }
  sameAsAgencyAddress: boolean
}

interface AgencyFormProps {
  isEditMode?: boolean
  isCreateMode?: boolean // New: Explicitly indicate create mode
  initialValues?: Partial<IFormValues>
  onCancel?: () => void
  isSubmitting?: boolean
  isValid?: boolean
  isDirty?: boolean
  showParentAgencyField?: boolean // Control visibility of parent agency field
  parentAgencyOptions?: Array<{id: string; name: string}> // Options for parent agency
  setIsEditing?: (isEditing: boolean) => void // Optional: not needed in create mode
  isBranch?: boolean // Show markup field and adjust labels for branch creation
}

export const AgencyForm: React.FC<AgencyFormProps> = ({
  isEditMode = false,
  isCreateMode = false,
  onCancel,
  isSubmitting = false,
  isValid = true,
  isDirty = false,

  showParentAgencyField = false,
  parentAgencyOptions = [],
  setIsEditing,
  isBranch = false,
}) => {
  const {values, setFieldValue} = useFormikContext<IFormValues>()
  const location = useLocation()
  const isNewBranch = location.pathname.includes('/branches/new')
  const {canEdit} = usePermission('manage_agencies')

  // In create mode, fields should always be enabled
  // In edit mode, fields are enabled based on isEditMode state
  const fieldsEnabled = isCreateMode || isEditMode

  const handleSameAddressChange = React.useCallback(
    (checked: boolean) => {
      void setFieldValue('sameAsAgencyAddress', checked)
      if (checked && values.address.line1) {
        // Copy the exact agency address object to billing address
        void setFieldValue('billingAddress', values.address)
      } else if (!checked) {
        // Clear billing address when unchecked
        void setFieldValue('billingAddress', {
          line1: '',
          city: '',
          state: '',
          zipCode: '',
          display: '',
        })
      }
    },
    [setFieldValue, values.address],
  )

  // Watch for changes to agency address to keep billing address in sync when checkbox is checked
  React.useEffect(() => {
    if (values.sameAsAgencyAddress && values.address.line1) {
      // Checkbox is checked and agency address exists, keep billing address in sync
      void setFieldValue('billingAddress', values.address)
    }
  }, [values.address?.line1, values.address?.display, values.sameAsAgencyAddress, setFieldValue])

  // Watch for manual changes to billing address to uncheck the checkbox
  React.useEffect(() => {
    if (values.sameAsAgencyAddress && values.billingAddress.line1 && values.address.line1) {
      // Check if billing address has been manually changed
      const addressesAreSame =
        values.billingAddress.line1 === values.address.line1 &&
        values.billingAddress.city === values.address.city &&
        values.billingAddress.state === values.address.state &&
        values.billingAddress.zipCode === values.address.zipCode

      if (!addressesAreSame) {
        // Billing address was manually changed, uncheck the checkbox
        void setFieldValue('sameAsAgencyAddress', false)
      }
    }
  }, [
    values.billingAddress?.line1,
    values.billingAddress?.display,
    values.address?.line1,
    values.sameAsAgencyAddress,
    setFieldValue,
  ])

  return (
    <FormContainer>
      <HeaderAndButtonContainerBorder>
        <SoloSubTabText>Agency Information</SoloSubTabText>

        {/* Edit button - only show when NOT in create mode and NOT editing */}
        {!isCreateMode && !isEditMode && setIsEditing && canEdit && (
          <EditButton onClick={() => setIsEditing(true)} startIcon={<Edit size={16} />}>
            Edit
          </EditButton>
        )}
      </HeaderAndButtonContainerBorder>

      {isCreateMode && (
        <ColoredTextPleaseEnter>
          Please enter all required agency information
        </ColoredTextPleaseEnter>
      )}

      <GridContainer>
        {/* Left Column */}
        <LeftColumn>
          <FieldGroup>
            <FormLabel>
              Agency Name <RequiredAsterisk />
            </FormLabel>
            <FormikTextField
              data-testid="agencyForm-name-input"
              aria-label="agencyForm-name-input"
              name="name"
              placeholder="JS North America"
              fullWidth
              disabled={!fieldsEnabled}
            />
          </FieldGroup>

          <PhoneExtensionGrid>
            <FieldGroup>
              <FormLabel>
                Agency Phone <RequiredAsterisk />
              </FormLabel>
              <PhoneInput
                data-testid="agencyForm-phoneNumber-input"
                aria-label="agencyForm-phoneNumber-input"
                name="phoneNumber"
                placeholder="(555) 123-4567"
                fullWidth
                disabled={!fieldsEnabled}
              />
            </FieldGroup>
            <FieldGroup>
              <FormLabel>Extension</FormLabel>
              <FormikTextField
                data-testid="agencyForm-phoneExtension-input"
                aria-label="agencyForm-phoneExtension-input"
                name="phoneExtension"
                placeholder=""
                fullWidth
                disabled={!fieldsEnabled}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                onInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const target = e.target as HTMLInputElement
                  target.value = target.value.replace(/[^0-9]/g, '')
                }}
              />
            </FieldGroup>
          </PhoneExtensionGrid>

          <FieldGroup>
            <FormLabel>Agency Website</FormLabel>
            <FormikTextField
              data-testid="agencyForm-website-input"
              aria-label="agencyForm-website-input"
              name="website"
              placeholder="www.forever21.com"
              fullWidth
              disabled={!fieldsEnabled}
            />
          </FieldGroup>

          {/* {showParentAgencyField && (
            <FieldGroup>
              <FormLabel>Parent Agency</FormLabel>
              <FormikSelect
                name="parentAgencyId"
                fullWidth
                displayEmpty
                disabled={!fieldsEnabled}
                inputProps={{
                  MenuProps: {
                    style: {maxHeight: 300},
                    id: 'id-menu',
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                  },
                }}
                SelectProps={{
                  displayEmpty: true,
                }}>
                <MenuItem value="">
                  <em>None (Top-level agency)</em>
                </MenuItem>
                {parentAgencyOptions.map((agency) => (
                  <MenuItem key={agency.id} value={agency.id}>
                    <Tooltip title={agency.name}>
                      <EllipsisText>{agency.name}</EllipsisText>
                    </Tooltip>
                  </MenuItem>
                ))}
              </FormikSelect>
            </FieldGroup>
          )} */}
        </LeftColumn>

        {/* Right Column */}
        <RightColumn>
          <TwoColumnGrid>
            <FieldGroup>
              <FormLabel>
                Admin First Name <RequiredAsterisk />
              </FormLabel>
              <FormikTextField
                data-testid="agencyForm-adminFirstName-input"
                aria-label="agencyForm-adminFirstName-input"
                name="adminFirstName"
                placeholder="John"
                fullWidth
                disabled={!fieldsEnabled}
              />
            </FieldGroup>
            <FieldGroup>
              <FormLabel>
                Admin Last Name <RequiredAsterisk />
              </FormLabel>
              <FormikTextField
                data-testid="agencyForm-adminLastName-input"
                aria-label="agencyForm-adminLastName-input"
                name="adminLastName"
                placeholder="Smith"
                fullWidth
                disabled={!fieldsEnabled}
              />
            </FieldGroup>
          </TwoColumnGrid>

          <FieldGroup>
            <FormLabel>
              Admin Email Address <RequiredAsterisk />
            </FormLabel>
            <FormikTextField
              data-testid="agencyForm-adminEmail-input"
              aria-label="agencyForm-adminEmail-input"
              name="adminEmail"
              placeholder="john_smith@email.com"
              fullWidth
              disabled={!isCreateMode && !isNewBranch}
            />
          </FieldGroup>

          <TwoColumnGrid>
            <FieldGroup>
              <FormLabel>
                MSP <RequiredAsterisk />:
              </FormLabel>
              <ToggleContainer>
                <ToggleLabel>Off</ToggleLabel>
                <StyledSwitch
                  data-testid="agencyForm-isMsp-switch"
                  aria-label="agencyForm-isMsp-switch"
                  checked={values.isMsp}
                  onChange={(e) => setFieldValue('isMsp', e.target.checked)}
                  // Parent agencies: MSP always ON and not clickable
                  // Branch agencies: MSP always OFF and not clickable
                  disabled={true}
                />
                <ToggleLabel>On</ToggleLabel>
              </ToggleContainer>
            </FieldGroup>

            <FieldGroup>
              <FormLabel>
                EOR <RequiredAsterisk />:
              </FormLabel>
              <ToggleContainer>
                <ToggleLabel>Off</ToggleLabel>
                <StyledSwitch
                  data-testid="agencyForm-isEor-switch"
                  aria-label="agencyForm-isEor-switch"
                  checked={values.isEor}
                  onChange={(e) => setFieldValue('isEor', e.target.checked)}
                  // Parent agencies: EOR always ON and not clickable
                  // Branch agencies: EOR is clickable (can toggle ON/OFF)
                  disabled={!isBranch ? true : !fieldsEnabled}
                />
                <ToggleLabel>On</ToggleLabel>
              </ToggleContainer>
            </FieldGroup>
          </TwoColumnGrid>
        </RightColumn>
      </GridContainer>

      <Divider />

      {/* Address Section */}
      <AddressContainer>
        <FieldGroup>
          <FormLabel>
            Location Address <RequiredAsterisk />
          </FormLabel>
          <FormikGooglePlacesAutocomplete
            data-testid="agencyForm-address-input"
            aria-label="agencyForm-address-input"
            name="address"
            address={values.address}
            setFieldValue={setFieldValue}
            isCityStateBoolean={false}
            initialEditValues={undefined}
            disabled={!fieldsEnabled}
          />
        </FieldGroup>

        <FieldGroup>
          <FormLabel>
            Billing Address <RequiredAsterisk />
          </FormLabel>
          <FormikGooglePlacesAutocomplete
            key={`billing-${values.sameAsAgencyAddress ? 'same' : 'different'}`}
            data-testid="agencyForm-billingAddress-input"
            aria-label="agencyForm-billingAddress-input"
            name="billingAddress"
            address={values.billingAddress}
            setFieldValue={setFieldValue}
            isCityStateBoolean={false}
            initialEditValues={undefined}
            disabled={values.sameAsAgencyAddress || !fieldsEnabled}
          />
          <CheckboxContainer>
            <FormControlLabel
              control={
                <StyledCheckbox
                  data-testid="agencyForm-sameAsAgencyAddress-checkbox"
                  aria-label="agencyForm-sameAsAgencyAddress-checkbox"
                  checked={values.sameAsAgencyAddress}
                  onChange={(e) => handleSameAddressChange(e.target.checked)}
                  disabled={!fieldsEnabled}
                />
              }
              label="Same as Agency Address"
            />
          </CheckboxContainer>
        </FieldGroup>
      </AddressContainer>

      {/* Markup field - only show for branches */}
      {isNewBranch && (
        <MarkupContainer>
          <FormLabel>
            Mark Up <RequiredAsterisk />
          </FormLabel>
          <MarkupInputWrapper>
            <FormikTextField
              data-testid="agencyForm-markupPercentage-input"
              aria-label="agencyForm-markupPercentage-input"
              name="markupPercentage"
              type="number"
              inputProps={{min: 0, max: 100}}
              placeholder="0"
              disabled={!isNewBranch}
              sx={{
                'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                'input[type=number]': {
                  MozAppearance: 'textfield',
                },
              }}
            />
            <PercentageSymbol>%</PercentageSymbol>
          </MarkupInputWrapper>
        </MarkupContainer>
      )}

      {/* Agency form buttons - show in both create and edit modes */}
      {fieldsEnabled && (
        <ButtonContainer
          style={{paddingBottom: '24px', paddingRight: '24px', justifyContent: 'center'}}>
          <Button
            variant="secondary"
            data-testid="agencyForm-cancel-button"
            aria-label="agencyForm-cancel-button"
            type="button"
            onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="agencyForm-save-button"
            aria-label="agencyForm-save-button"
            type="submit"
            disabled={isSubmitting || !isValid || !isDirty}>
            {isCreateMode ? 'Create Agency' : 'Save Changes'}
          </Button>
        </ButtonContainer>
      )}
    </FormContainer>
  )
}

export type {IFormValues as AgencyFormValues}
